/* eslint-disable react-hooks/exhaustive-deps */
import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import CopyableTag from "components/CopyableTag";
import CustomButton from "components/CustomButton";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { sortArray } from "utils/miscUtils";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import { defaultPageNo, defaultPageSize } from "utils/constants";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import sessionHelper from "services/sessionHelper";
import {
  createTutorialConfig,
  deleteTutorialConfig,
  getTutorialList,
} from "services/tutorialService";
import { PermittedRoles } from "utils/permissions";

const useStyles = makeStyles((theme: any) => ({
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
  idCell: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "40px",
  },
  statusChip: {
    fontWeight: "600 !important",
    fontSize: "11px !important",
    height: "22px !important",
    minWidth: "70px",
  },
}));

interface RowActionsProps {
  rowId?: any;
  data: any;
  actionHandlers: Record<string, any>;
}

const RowActions = ({ data, actionHandlers }: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => setAnchorEl(null);
  const handleClick = (event: any) => setAnchorEl(event.currentTarget);

  const handleEdit = () => {
    setAnchorEl(null);
    navigate(
      configData.routes.tutorials.edit.replace(
        ":operatorId",
        data?.operatorId || data?._id,
      ),
    );
  };

  const handleDelete = () => {
    setAnchorEl(null);
    actionHandlers?.onDelete?.(data);
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.TUTORIAL.DELETE_ROLES,
  );

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Manage Videos
        </MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete Config
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const TutorialsPage = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState<any[]>([]);
  const [filteredList, setFilteredList] = useState<any[]>([]);
  const [orderDirection, setOrderDirection] = useState("asc");
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState<any>(null);
  const [creating, setCreating] = useState(false);

  const searchValue = searchParams.get("search") || "";
  const pageNo = Number(searchParams.get("pageNo")) || defaultPageNo;
  const pageSize = Number(searchParams.get("pageSize")) || defaultPageSize;

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.TUTORIAL.UPDATE_ROLES,
  );

  const onChangePage = (_pageNo: number) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("pageNo", String(_pageNo));
      return p;
    });
  };

  const onPageSizeChange = (event: any) => {
    const newSize = Number(event?.target?.value ?? defaultPageSize);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("pageNo", "1");
      p.set("pageSize", String(newSize));
      return p;
    });
  };

  const onColumnClick = (column: any) => {
    const _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const onSearch = (value: any) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) {
        p.set("search", value);
      } else {
        p.delete("search");
      }
      p.set("pageNo", String(defaultPageNo));
      return p;
    });
  };

  const buildListData = (configs: any[]) => {
    const operatorMap = new Map<string, any>();
    (sessionHelper.allowedOperators || []).forEach((op: any) => {
      operatorMap.set(op._id, op);
    });

    return (configs || []).map((cfg: any) => {
      const id = cfg.operatorId?._id || cfg.operatorId;
      const op = operatorMap.get(id);
      return {
        _id: cfg._id,
        operatorId: id,
        operatorName: op?.operatorName || id,
        platformName: op?.platformName || "-",
        videoCount: cfg.videos?.length || 0,
        languages:
          cfg.videos?.map((v: any) => v.languageCode).join(", ") || "-",
      };
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getTutorialList();
      const items = resp?.data?.items || resp?.data || [];
      setListData(buildListData(items));
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const getAvailableOperators = () => {
    const existingIds = new Set(listData.map((item) => item.operatorId));
    return (sessionHelper.allowedOperators || []).filter(
      (op: any) => !existingIds.has(op._id) && op.isActive !== false,
    );
  };

  const handleCreate = async () => {
    if (!selectedOperator) return;
    setCreating(true);
    try {
      await createTutorialConfig(selectedOperator._id);
      showSnackbar("Tutorial config created successfully", "success");
      setCreateDialogOpen(false);
      setSelectedOperator(null);
      fetchData();
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (data: any) => {
    if (
      !window.confirm(
        `Delete tutorial config for ${data.operatorName || data.operatorId}?`,
      )
    )
      return;
    try {
      await deleteTutorialConfig(data.operatorId);
      showSnackbar("Tutorial config deleted successfully", "success");
      fetchData();
    } catch (error: any) {
      showSnackbar(error.message, "error");
    }
  };

  // Apply client-side search filter
  useEffect(() => {
    if (!searchValue) {
      setFilteredList(listData);
    } else {
      const lower = searchValue.toLowerCase();
      setFilteredList(
        listData.filter(
          (item) =>
            item.operatorName?.toLowerCase().includes(lower) ||
            item.operatorId?.toLowerCase().includes(lower),
        ),
      );
    }
  }, [listData, searchValue]);

  useEffect(() => {
    fetchData();
  }, []);

  const columns = [
    {
      id: "operatorId",
      label: "Operator ID",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      renderCell: (data: any) => (
        <Box className={styles.idCell}>
          <CopyableTag value={data.operatorId} />
        </Box>
      ),
    },
    {
      id: "operatorName",
      label: "Operator",
      isSortable: true,
      alignHead: "left",
      alignBody: "left",
      renderCell: (data: any) => (
        <Box sx={{ fontWeight: 700, fontSize: "13px" }}>
          {data.operatorName}
        </Box>
      ),
    },
    {
      id: "videoCount",
      label: "Videos",
      isSortable: true,
      alignHead: "center",
      alignBody: "center",
      renderCell: (data: any) => (
        <Chip
          label={data.videoCount}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 600,
            fontSize: "12px",
            minWidth: "36px",
            borderColor: data.videoCount > 0 ? "#1976d2" : "#bdbdbd",
            color: data.videoCount > 0 ? "#1976d2" : "#494949",
          }}
        />
      ),
    },

    ...(hasEditPermission
      ? [
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]
      : []),
  ];

  return (
    <ContentShell
      title="Tutorials"
      loading={loading}
      actions={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <SearchInput
            value={searchValue}
            placeholder="Search operator..."
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.TUTORIAL.CREATE_ROLES) && (
            <CustomButton onClick={() => setCreateDialogOpen(true)}>
              Create
            </CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <Table
          columns={columns}
          data={filteredList.slice((pageNo - 1) * pageSize, pageNo * pageSize)}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={filteredList.length}
          highlightAltRow={true}
          disableMaxHeight={true}
          actions={hasEditPermission ? RowActions : undefined}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          onColumnClick={onColumnClick}
          actionHandlers={{ onDelete: handleDelete }}
        />
      </Box>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Tutorial Config</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={getAvailableOperators()}
            getOptionLabel={(op: any) => `${op.operatorName} (${op._id})`}
            value={selectedOperator}
            onChange={(_e, value) => setSelectedOperator(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Operator"
                margin="normal"
                fullWidth
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => {
              setCreateDialogOpen(false);
              setSelectedOperator(null);
            }}
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handleCreate}
            disabled={!selectedOperator || creating}
          >
            {creating ? "Creating..." : "Create"}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </ContentShell>
  );
};

export default TutorialsPage;
